<template>
  <div>
    <v-layout row>
      <v-flex>
        <HeaderBar>
          <v-icon slot="icon">people_outline</v-icon>
          Employees
          <template slot="buttons">
            <v-btn
              v-if="user.managerOrHigher"
              :to="{ name: 'deactivatedEmployees' }"
              color="grey"
              class="white--text"
            >
              Deactivated Employees
            </v-btn>
            <v-btn
              v-if="user.managerOrHigher"
              :to="{ name: 'employeeWatchListIndex' }"
              color="primary"
            >
              Go to watch lists
            </v-btn>
            <v-btn
              v-if="user.managerOrHigher"
              to="/hr/employees/new"
              color="success"
            >
              <v-icon left>
                add
              </v-icon>
              Add Team Member
            </v-btn>
          </template>
        </HeaderBar>

        <v-card>
          <v-card-title>
            <h3>Staffing</h3>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
            >
            </v-text-field>
          </v-card-title>
          <v-data-table
            :loading="employees.length == 0"
            :headers="headers"
            :items="filteredEmployees"
            item-key="id"
            :search="search"
            :pagination.sync="defaultSort"
            class="elevation-1"
          >
            <template slot="items" slot-scope="{ item }">
              <tr>
                <td>
                  <router-link
                    :to="{
                      name: 'employeeDetail',
                      params: { employeeId: item.id }
                    }"
                    class="unstyled-link"
                    target="_blank"
                  >
                    <v-icon>launch</v-icon>
                  </router-link>
                </td>
                <td @click="openEmployee(item.id)">
                  <EmployeeName
                    :employee="item"
                    :showColor="user.managerOrHigher"
                  />
                </td>
                <td>
                  <v-chip
                    v-for="(position, idx) in (item.position_names || []).slice(0, 3)"
                    :key="`pos-${item.id}-${idx}`"
                    small
                    color="grey lighten-3"
                    text-color="grey darken-2"
                    class="font-weight-bold"
                  >
                    {{ position }}
                  </v-chip>
                  <v-chip
                    v-if="item.new_grad"
                    small
                    color="blue darken-2"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    NG
                  </v-chip>
                  <v-chip
                    v-if="item.advanced_assistant"
                    small
                    color="amber darken-3"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    ADV
                  </v-chip>
                </td>
                <td>{{ item.status }}</td>
                <td>{{ item.standing }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout row align-content-space-between align-center class="mt-4">
      <v-flex>
        <h3 class="all-caps mr-2">Filter</h3>
      </v-flex>
      <v-flex>
        <v-select
          :items="managerFilterWithNone"
          item-text="name"
          item-value="id"
          return-object
          label="By Manager"
          v-model="managerFilter"
          clearable
          class="mr-2"
        >
        </v-select>
      </v-flex>
      <v-flex>
        <v-select
          :items="filters.statuses"
          item-text="name"
          item-value="name"
          return-object
          label="Status"
          v-model="statusFilter"
          clearable
          class="mr-2"
        >
        </v-select>
      </v-flex>
      <v-flex>
        <v-select
          :items="filters.positions"
          item-text="name"
          item-value="id"
          return-object
          label="Position"
          v-model="positionFilter"
          clearable
          class="mr-2"
        >
        </v-select>
      </v-flex>
      <v-layout column align-baseline>
        <v-flex>
          <v-select
            :items="filters.skills"
            item-text="name"
            item-value="id"
            return-object
            label="Skill"
            v-model="skillFilter"
            clearable
            multiple
            chips
            class="mr-2"
          >
          </v-select>
        </v-flex>
        <v-flex row flex-grow-0 v-if="skillFilter.length > 0">
          <v-radio-group row v-model="matchAllSkills">
            <v-radio :value="false" label="Match Any Skills"></v-radio>
            <v-radio :value="true" label="Match All Skills"></v-radio>
          </v-radio-group>
        </v-flex>
      </v-layout>
      <v-flex shrink class="mr-2">
        <v-select
          label="State"
          :items="states"
          v-model="selectedStateFilter"
          clearable
        ></v-select>
      </v-flex>
      <v-flex>
        <v-autocomplete
          :items="filters.locations"
          item-text="name"
          item-value="id"
          return-object
          label="Location"
          v-model="locationFilter"
          clearable
          class="mr-2"
        >
        </v-autocomplete>
      </v-flex>
      <v-flex>
        <v-autocomplete
          :items="filters.geotags"
          label="Geo Tag"
          v-model="geotagFilter"
          clearable
          class="mr-2"
        >
        </v-autocomplete>
      </v-flex>
      <v-flex grow>
        <span @click="resetFilters" class="pointer">
          <v-icon>cancel</v-icon>
          Reset Filters
        </span>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { mapState } from "vuex";
import EmployeeName from "@/components/hr/employee-name.vue";
import HeaderBar from "@/components/base/header-bar.vue";
import collectionsApi from "@/api/collections";

export default {
  components: {
    EmployeeName,
    HeaderBar
  },
  created() {
    this.setUp();
    this.employeesLoading = true;
    this.$store.dispatch("employees/getAll", this.user.id).finally(r => {
      this.employeesLoading = false;
    });
    this.$store.dispatch("hr/getFilters", this.user.id);
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    ...mapState("employees", ["employees"]),
    ...mapState("hr", ["filters"]),
    managerFilterWithNone: function() {
      const managers = this.filters.managers.slice();
      managers.push({ name: "No Manager", id: null });
      return managers;
    },
    filteredEmployees: function() {
      const filtered = this.employees
        .filter(employee => {
          return (
            !this.statusFilter || employee.status === this.statusFilter.name
          );
        })
        .filter(employee => {
          if (this.managerFilter) {
            return employee.manager_id == this.managerFilter.id;
          } else {
            return employee;
          }
        })
        .filter(employee => {
          const positionIds = employee.employeePositions.map(p => p.id);
          return (
            !this.positionFilter ||
            positionIds.indexOf(this.positionFilter.id) > -1
          );
        })
        .filter(employee => {
          // if not filtering on skills, return all employees
          if (this.skillFilter.length === 0) {
            return true;
          }

          const employeeSkillIds = employee.skillIds.map(s => s);

          if (this.matchAllSkills) {
            // only want true for employees who have all of these skills
            return this.skillFilter
              .map(s => s.id)
              .every(skillId => employeeSkillIds.includes(skillId));
          } else {
            // return true if employee has any of these skills
            return employeeSkillIds.find(eSkillId => {
              return this.skillFilter.map(s => s.id).includes(eSkillId);
            });
          }
        })
        .filter(employee => {
          const locationIds = employee.teams.map(t => t.id);
          return (
            !this.locationFilter ||
            locationIds.indexOf(this.locationFilter.id) > -1
          );
        })
        .filter(employee => {
          return (
            !this.selectedStateFilter ||
            employee.state === this.selectedStateFilter
          );
        })
        .filter(employee => {
          return !this.ineligibleTeamMembers.includes(employee.id);
        })
        .filter(employee => {
          return (
            !this.geotagFilter ||
            employee.geotag_states.includes(this.geotagFilter)
          );
        });
      return filtered;
    }
  },
  methods: {
    resetFilters() {
      this.statusFilter = "";
      this.managerFilter = "";
      this.positionFilter = "";
      this.selectedStateFilter = null;
      this.skillFilter = [];
      this.matchAllSkills = false;
      this.locationFilter = "";
      this.geotagFilter = "";
    },
    openEmployee(id) {
      this.$router.push({ name: "employeeDetail", params: { employeeId: id } });
    },
    setUp() {
      this.clientWorksiteIds = this.user.clientWorksiteIds;
      // Get ineligible team members
      if (this.clientWorksiteIds.length > 0) {
        this.clientWorksiteIds.forEach(locationId =>
          this.$store
            .dispatch("teams/getIneligibleAtLocation", locationId)
            .then(ineligibleTeamMembers => {
              ineligibleTeamMembers.forEach(ineligibleTeamMember => {
                this.ineligibleTeamMembers.push(ineligibleTeamMember.id);
              });
            })
            .catch(e => {
              console.log(e);
            })
            .finally(r => {})
        );
      }
    }
  },
  data: function() {
    return {
      ineligibleTeamMembers: [],
      clientWorksiteIds: [],

      search: "",
      statusFilter: "",
      managerFilter: "",
      positionFilter: "",
      skillFilter: [],
      matchAllSkills: false,
      locationFilter: "",
      geotagFilter: "",
      states: collectionsApi.getStates(),

      selectedStateFilter: null,
      employeesLoading: null,
      defaultSort: {
        sortBy: "last_name",
        ascending: true,
        rowsPerPage: 10
      },
      headers: [
        { text: "" },
        {
          text: "Name",
          value: "display_name"
        },
        {
          text: "Position",
          value: "position_names",
          sortable: false
        },
        {
          text: "Status",
          value: "status"
        },
        {
          text: "Standing",
          value: "standing"
        }
      ],
      menuData: {
        messages: [
          "Any message you like can go here",
          "Will rotate through list of actual messages"
        ],
        employeeName: "Brad",
        hasMail: true,
        hasTasksDue: false
      }
    };
  }
};
</script>

<style scoped></style>
